import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
// Ajout de l'icône Edit
import { Plus, Trash2, Copy, ExternalLink, MousePointer, Search, Edit } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Shares = () => {
  const { t, API } = useApp();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cabinets, setCabinets] = useState([]); 
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Modifié : stocke l'objet share en cours d'édition (null si création)
  const [editingShare, setEditingShare] = useState(null);
  const [selectedShareToDelete, setSelectedShareToDelete] = useState(null);

  const [formData, setFormData] = useState({
    friendly_name: '',
    expires_in_days: 14,
    no_expiration: false,
    cabinet_ids: []
  });
  const [searchCab, setSearchCab] = useState('');

  useEffect(() => {
    fetchShares();
  }, []);

  const fetchShares = async () => {
    try {
      const response = await axios.get(`${API}/shares`);
      setShares(response.data);
    } catch (error) {
      toast.error("Failed to load share lists");
    } finally {
      setLoading(false);
    }
  };

  const fetchCabinets = async () => {
    if (cabinets.length > 0) return; 
    try {
      const response = await axios.get(`${API}/cabinets`);
      setCabinets([...response.data].sort((a, b) => (a.friendly_name || '').localeCompare(b.friendly_name || '', undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error(error);
    }
  };

  // Modifié : gère l'ouverture pour CREATE ou EDIT
  const openDialog = (shareToEdit = null) => {
    fetchCabinets(); // Assure que la liste des cabs est chargée
    
    if (shareToEdit) {
      setEditingShare(shareToEdit);

      setFormData({
        friendly_name: shareToEdit.friendly_name,
        expires_in_days: 14,
        no_expiration: !shareToEdit.expires_at,
        cabinet_ids: shareToEdit.cabinet_ids || (shareToEdit.cabinets ? shareToEdit.cabinets.map(c => c.id) : [])
      });
    } else {
      setEditingShare(null);
      setFormData({ friendly_name: '', expires_in_days: 14, no_expiration: false, cabinet_ids: [] });
    }
    
    setShowCreateDialog(true);
  };

  const handleSave = async () => {
    if (!formData.friendly_name || formData.cabinet_ids.length === 0) {
      toast.error("Name and at least one cabinet are required");
      return;
    }

    try {
      if (editingShare) {
        // UPDATE (PUT)
        // Note: Assurez-vous que votre backend supporte PUT /shares/{id}
        await axios.put(`${API}/shares/${editingShare.id}`, formData);
        toast.success("Share list updated");
      } else {
        // CREATE (POST)
        await axios.post(`${API}/shares`, formData);
        toast.success("Share list created");
      }
      
      setShowCreateDialog(false);
      fetchShares();
    } catch (error) {
      console.error(error);
      toast.error(editingShare ? "Error updating share list" : "Error creating share list");
    }
  };

  const handleDelete = async () => {
    if (!selectedShareToDelete) return;
    try {
      await axios.delete(`${API}/shares/${selectedShareToDelete.id}`);
      toast.success("Deleted successfully");
      setShowDeleteDialog(false);
      fetchShares();
    } catch (error) {
      toast.error("Error deleting");
    }
  };

  const copyToClipboard = (token) => {
    const url = `${window.location.origin}/s/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const filteredCabinets = cabinets.filter(c => 
    c.friendly_name.toLowerCase().includes(searchCab.toLowerCase()) || 
    c.unique_id.toLowerCase().includes(searchCab.toLowerCase())
  );

  const toggleCabinet = (id) => {
    setFormData(prev => {
      const exists = prev.cabinet_ids.includes(id);
      return {
        ...prev,
        cabinet_ids: exists 
          ? prev.cabinet_ids.filter(cid => cid !== id)
          : [...prev.cabinet_ids, id]
      };
    });
  };

  const selectAll = () => {
    const allFilteredIds = filteredCabinets.map(c => c.id);
    setFormData(prev => ({
      ...prev,
      cabinet_ids: [...new Set([...prev.cabinet_ids, ...allFilteredIds])]
    }));
  };

  const unselectAll = () => {
    const allFilteredIds = filteredCabinets.map(c => c.id);
    setFormData(prev => ({
      ...prev,
      cabinet_ids: prev.cabinet_ids.filter(id => !allFilteredIds.includes(id))
    }));
  };

  const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString() : 'Never');

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            Shared Lists
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-xs">
            Manage your public shared links
          </p>
        </div>
        <Button onClick={() => openDialog(null)} className="w-full md:w-auto font-display uppercase tracking-[0.16em] glow-amber">
          <Plus className="w-4 h-4 mr-2" />
          Create New Share
        </Button>
      </div>

      {/* List */}
      <Card className="bezel overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px] eyebrow text-[10px] text-muted-foreground">Name</TableHead>
                  <TableHead className="eyebrow text-[10px] text-muted-foreground">Status</TableHead>
                  <TableHead className="eyebrow text-[10px] text-muted-foreground">Items</TableHead>
                  <TableHead className="hidden md:table-cell eyebrow text-[10px] text-muted-foreground">Clicks</TableHead>
                  <TableHead className="hidden md:table-cell eyebrow text-[10px] text-muted-foreground">Expires</TableHead>
                  <TableHead className="hidden lg:table-cell eyebrow text-[10px] text-muted-foreground">Created By</TableHead>
                  <TableHead className="text-right eyebrow text-[10px] text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shares.map((share) => (
                  <TableRow key={share.id} className={`hover:bg-muted/40 transition-colors ${!share.is_active ? 'opacity-60 bg-muted/30' : ''}`}>
                    
                    {/* Name Cell */}
                    <TableCell>
                      <div className="font-medium truncate max-w-[150px] md:max-w-none" title={share.friendly_name}>
                        {share.friendly_name}
                      </div>
                      <div className="md:hidden text-xs text-muted-foreground mt-1 flex flex-col gap-0.5">
                        <span className="flex items-center gap-1">
                           <MousePointer className="w-2.5 h-2.5" /> <span className="tabular-nums">{share.click_count}</span> clicks
                        </span>
                        <span className="font-mono">Exp: {formatDate(share.expires_at)}</span>
                      </div>
                    </TableCell>

                    <TableCell>
                      {share.is_active ? (
                        <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] md:text-xs px-1.5 md:px-2.5">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-secondary/15 text-secondary border-secondary/30 text-[10px] md:text-xs px-1.5 md:px-2.5">
                          Expired
                        </Badge>
                      )}
                    </TableCell>
                    
                    <TableCell className="text-sm whitespace-nowrap">
                       <span className="tabular-nums">{share.item_count}</span> <span className="hidden sm:inline">cabinets</span>
                    </TableCell>

                    {/* Desktop Only Columns */}
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MousePointer className="w-3 h-3" /> <span className="tabular-nums">{share.click_count}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-sm whitespace-nowrap">
                        {formatDate(share.expires_at)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                        {share.created_by}
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => copyToClipboard(share.token)} title="Copy Link">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" asChild title="Open Link" className="hidden sm:inline-flex">
                          <a href={`/s/${share.token}`} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                        
                        {/* BOUTON EDIT AJOUTÉ */}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openDialog(share)}
                          title="Edit Share"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>

                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => { setSelectedShareToDelete(share); setShowDeleteDialog(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                
                {shares.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No active shares found. Create one to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg w-[95vw] md:w-full"> 
          <DialogHeader>
            <DialogTitle>{editingShare ? 'Edit Share List' : 'Create Public Share List'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>List Name</Label>
              <Input 
                value={formData.friendly_name} 
                onChange={(e) => setFormData({...formData, friendly_name: e.target.value})}
                placeholder="e.g. Client Selection" 
              />
            </div>
            
            <div className="space-y-2">
              <Label className={formData.no_expiration ? 'text-muted-foreground' : ''}>
                 {editingShare ? 'Extend expiration by (days)' : 'Expires in (days)'}
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.expires_in_days}
                disabled={formData.no_expiration}
                onChange={(e) => setFormData({...formData, expires_in_days: parseInt(e.target.value) || 0})}
              />

              <div
                className="flex items-center gap-2.5 pt-2 cursor-pointer"
                onClick={() => setFormData({...formData, no_expiration: !formData.no_expiration})}
              >
                <Checkbox
                  checked={formData.no_expiration}
                  onCheckedChange={(c) => setFormData({...formData, no_expiration: !!c})}
                />
                <span className="text-sm select-none">No expiration (permanent link)</span>
              </div>

              {editingShare && !formData.no_expiration && (
                <p className="text-xs text-muted-foreground">
                  Current expiration: {formatDate(editingShare.expires_at)}.
                  Enter days to extend, or leave to keep current.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex flex-col gap-2 mb-2">
                <div className="flex justify-between items-center">
                  <Label>Select Cabinets ({formData.cabinet_ids.length})</Label>
                  <div className="relative w-32 md:w-40">
                    <Search className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
                    <Input 
                      placeholder="Filter..." 
                      className="h-7 text-xs pl-7" 
                      value={searchCab}
                      onChange={(e) => setSearchCab(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 justify-end">
                   <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={selectAll}>Select All</Button>
                   <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2 text-muted-foreground hover:text-foreground" onClick={unselectAll}>Unselect All</Button>
                </div>
              </div>

              <Card className="bezel border">
                <ScrollArea className="h-[200px] w-full p-2">
                  <div className="space-y-1">
                    {filteredCabinets.map(cab => (
                      <div 
                        key={cab.id} 
                        className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded-md cursor-pointer active:bg-muted"
                        onClick={() => toggleCabinet(cab.id)}
                      >
                        <Checkbox 
                          checked={formData.cabinet_ids.includes(cab.id)} 
                          onCheckedChange={() => toggleCabinet(cab.id)}
                        />
                        <div className="flex-1 text-sm overflow-hidden">
                          <span className="font-medium truncate block">{cab.friendly_name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{cab.unique_id}</span>
                        </div>
                      </div>
                    ))}
                    {filteredCabinets.length === 0 && <div className="text-center text-xs text-muted-foreground py-4">No cabinet found</div>}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingShare ? 'Update Share' : 'Create Link'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="w-[95vw] md:w-full">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Share Link?</AlertDialogTitle>
            <AlertDialogDescription>
              This link will immediately stop working. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Shares;
